
import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserProfile {
  id: string;
  name: string;
  avatarInitials: string;
  avatarUrl?: string;
}

// Global cache shared across all hook instances to prevent "Unknown User" flicker
const globalProfileCache: Record<string, UserProfile> = {};
const pendingRequests: Record<string, Promise<UserProfile>> = {};

export function useUserProfiles() {
  // Local state to trigger re-renders when cache updates
  const [, forceUpdate] = useState(0);
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Function to get a user profile by ID, using cache when available
  const getUserProfile = useCallback(async (userId: string): Promise<UserProfile> => {
    // Return from global cache if available
    if (globalProfileCache[userId]) {
      return globalProfileCache[userId];
    }

    // If there's already a pending request for this user, wait for it
    if (pendingRequests[userId]) {
      return pendingRequests[userId];
    }

    // Set loading state for this user ID
    setLoading(prev => ({ ...prev, [userId]: true }));

    // Create and store the promise
    pendingRequests[userId] = (async () => {
      try {
        // Use secure get_public_profile RPC function to access only safe public fields
        const { data: profileArray, error } = await supabase
          .rpc('get_public_profile', { profile_user_id: userId });
        
        if (error) throw error;

        const profile = profileArray && profileArray.length > 0 ? profileArray[0] : null;

        if (!profile) {
          throw new Error('Profile not found');
        }

        // Generate full name from first and last name
        const firstName = profile.first_name || '';
        const lastName = profile.last_name || '';
        const fullName = firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || lastName || 'Unknown User';

        // Generate proper initials from first and last name
        let initials = 'UN';
        if (firstName && lastName) {
          initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        } else if (firstName) {
          initials = firstName.substring(0, 2).toUpperCase();
        } else if (lastName) {
          initials = lastName.substring(0, 2).toUpperCase();
        }

        // Create profile object
        const userProfile: UserProfile = {
          id: profile.id,
          name: fullName,
          avatarInitials: initials,
          avatarUrl: profile.avatar_url || undefined
        };

        // Update global cache
        globalProfileCache[userId] = userProfile;
        
        // Trigger re-render
        forceUpdate(n => n + 1);

        return userProfile;
      } catch (error) {
        console.error("Error fetching user profile:", error);
        
        // Return a fallback profile with truncated user ID for deleted/missing profiles
        const shortId = userId.substring(0, 8);
        const fallbackProfile: UserProfile = {
          id: userId,
          name: `User (${shortId}...)`,
          avatarInitials: shortId.substring(0, 2).toUpperCase()
        };
        
        // Still cache the fallback to avoid repeated failed requests
        globalProfileCache[userId] = fallbackProfile;
        
        return fallbackProfile;
      } finally {
        // Clear loading state and pending request
        setLoading(prev => ({ ...prev, [userId]: false }));
        delete pendingRequests[userId];
      }
    })();

    return pendingRequests[userId];
  }, []);

  // Batch fetch multiple user profiles at once
  const getUserProfiles = useCallback(async (userIds: string[]): Promise<Record<string, UserProfile>> => {
    // Filter out IDs that are already in cache
    const idsToFetch = userIds.filter(id => !globalProfileCache[id]);
    
    if (idsToFetch.length === 0) {
      // All profiles are in cache, return them
      return userIds.reduce((acc, id) => {
        acc[id] = globalProfileCache[id];
        return acc;
      }, {} as Record<string, UserProfile>);
    }

    try {
      // Use secure get_public_profile RPC function for batch queries
      const profilePromises = idsToFetch.map(id =>
        supabase.rpc('get_public_profile', { profile_user_id: id })
      );
      const profileResults = await Promise.all(profilePromises);
      const profiles = profileResults.flatMap((result, index) =>
        result.data && result.data.length > 0
          ? [{ ...result.data[0], id: idsToFetch[index] }]
          : []
      );
      
      // Check for any errors in the results
      const firstError = profileResults.find(r => r.error)?.error;
      if (firstError) throw firstError;
      
      const newProfiles: Record<string, UserProfile> = {};
      
      // Process each fetched profile
      (profiles || []).forEach(profile => {
        const firstName = profile.first_name || '';
        const lastName = profile.last_name || '';
        const fullName = firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || lastName || 'Unknown User';
          
        // Generate proper initials from first and last name
        let initials = 'UN';
        if (firstName && lastName) {
          initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        } else if (firstName) {
          initials = firstName.substring(0, 2).toUpperCase();
        } else if (lastName) {
          initials = lastName.substring(0, 2).toUpperCase();
        }
          
        newProfiles[profile.id] = {
          id: profile.id,
          name: fullName,
          avatarInitials: initials,
          avatarUrl: profile.avatar_url || undefined
        };
      });

      // Add fallback profiles for IDs that weren't found (deleted users)
      idsToFetch.forEach(userId => {
        if (!newProfiles[userId]) {
          const shortId = userId.substring(0, 8);
          newProfiles[userId] = {
            id: userId,
            name: `User (${shortId}...)`,
            avatarInitials: shortId.substring(0, 2).toUpperCase()
          };
        }
      });

      // Update global cache with new profiles
      Object.assign(globalProfileCache, newProfiles);
      
      // Trigger re-render
      forceUpdate(n => n + 1);

      // Return all requested profiles (both from cache and newly fetched)
      return userIds.reduce((acc, id) => {
        acc[id] = newProfiles[id] || globalProfileCache[id] || {
          id,
          name: "Unknown User",
          avatarInitials: "UN"
        };
        return acc;
      }, {} as Record<string, UserProfile>);
    } catch (error) {
      console.error("Error batch fetching user profiles:", error);
      
      // Return fallback profiles for error cases
      return userIds.reduce((acc, id) => {
        if (!globalProfileCache[id]) {
          acc[id] = {
            id,
            name: "Unknown User",
            avatarInitials: "UN"
          };
        } else {
          acc[id] = globalProfileCache[id];
        }
        return acc;
      }, {} as Record<string, UserProfile>);
    }
  }, []);

  // Clear cache for a specific user (useful when profiles update)
  const invalidateProfile = useCallback((userId: string) => {
    delete globalProfileCache[userId];
    forceUpdate(n => n + 1);
  }, []);

  return { 
    getUserProfile, 
    getUserProfiles, 
    profileCache: globalProfileCache, 
    isLoading: loading,
    invalidateProfile
  };
}
